import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewChild,
  ElementRef,
  OnInit,
} from "@angular/core";
import { TaskDataService } from "../../../shared/services/task-data.service";
import { IBusinessDayQueryModel, IBusinessDayResultsModel, ITaskRequest, IVendorRequestData, TaskRequest } from "../../../modules/tasks/model/types";
import notify from "devextreme/ui/notify";
import validationEngine from "devextreme/ui/validation_engine";

import {
  getNotificationTimeout,
  sendNotificationMessage,
} from "../../../types/resource";
import { LoggingService } from "../../../framework/logging/logging.service";
import { SeverityLevel } from "../../../framework/logging/severity-level.model";
import { BehaviorSubject, lastValueFrom, Observable } from "rxjs";
import { filter, take } from "rxjs/operators";
import { MessageService } from "../../../shared/components/framework/services/message.service";
import dxDataGrid from "devextreme/ui/data_grid";
import { StaticDataService } from "../../../shared/services/static-data.service";
import { FormUtils } from "../../../shared/components/framework/services/form-builder.service";
import { DataGridSelectionService } from "../../../shared/services/data-grid-selection.service";
import { LoanDataBase } from "../baseclass/loandata-base";
import { DealDataBase } from "../baseclass/dealdata-base";
import { TreasuryDataService } from "../../../shared/services/treasury-data.service";
import { map } from "lodash";
import { IAccount } from "../../../modules/deal/model/types";

@Component({
  selector: "app-vendor-request",
  templateUrl: "./vendor-request.component.html",
  styleUrl: "./vendor-request.component.scss",
})
export class VendorRequestComponent extends LoanDataBase implements OnInit {
  @ViewChild("formElement") formElement!: ElementRef;

  @Input() taskTemplateName: string;
  @Input() task: ITaskRequest;

  @Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();

  // Change collectionAccounts to use BehaviorSubject for reactive updates
  private collectionAccountsSubject$ = new BehaviorSubject<string[]>([]);
  collectionAccounts$ = this.collectionAccountsSubject$.asObservable();

  // Make fifthBusinessDayFromToday reactive so date inputs update when the value is loaded
  private fifthBusinessDayFromTodaySubject$ = new BehaviorSubject<Date | null>(null);
  fifthBusinessDayFromToday$ = this.fifthBusinessDayFromTodaySubject$.asObservable();

  requestData: any = {};
  billableToClientData: any[];
  vendorData: IVendorRequestData;
  requestTypes: any[] = [];
  payeeData: any[] = [];
  disbursementTypes: any[] = [
    { name: 'Wire' },
    { name: 'Check' }
  ];
  isLoading: boolean = false;
  businessDayCalendars: any[];
  files: File[] = [];
  directors: any[];
  secondDirectors: any[];
  today: Date = new Date();
  fifthBusinessDayFromToday: Date;
  isUrgent: boolean = false;
  formItemsBehaviourSubject$ = new BehaviorSubject<any[]>([]);
  formItems$ = this.formItemsBehaviourSubject$.asObservable();
  formItems: any[] = [];
  formColCountBehaviourSubject$ = new BehaviorSubject<number>(2);
  formColCount$ = this.formColCountBehaviourSubject$.asObservable();
  formColCount: number = 2;
  mandatoryFiles: boolean = false;
  feeTypes: any[] = [];
  isEditable: boolean = false;
  currentRequestType: string = "";
  override showInactiveLoans: boolean = false;
  showInactiveDeals: boolean = false;
  isUpdatingLoanData: boolean = false;
  isUpdatingDealData: boolean = false;
  currentFormInstance: any = null;
  shouldDisableCheckbox: boolean = false;
  checkboxWidget: any = null;
  selectedDealLoanNumbers: string[] = [];



  private doNotExcludeSelectedLoans: boolean = false;

  constructor(
    taskDataService: TaskDataService,
    private readonly treasuryDataService: TreasuryDataService,
    private readonly loggingService: LoggingService,
    private readonly messageService: MessageService,
    private readonly selectionService: DataGridSelectionService,
    private readonly staticDataService: StaticDataService,
  ) {
    super(taskDataService);
    this.onRequestTypeSelectionChanged =
      this.onRequestTypeSelectionChanged.bind(this);
    this.onDealNumberSelectionChanged =
      this.onDealNumberSelectionChanged.bind(this);
    this.formItems$.subscribe((items: any[]) => {
      this.formItems = items;
    });

    this.formColCount$.subscribe((colcount: number) => {
      this.formColCount = colcount;
    });

    // Subscribe to collection accounts changes and update form config when data arrives
    this.collectionAccounts$.subscribe((accounts) => {
      if (accounts && accounts.length > 0 && this.currentRequestType) {
        // Update the form configuration with the new collection account data
        this.updateCollectionAccountInFormConfig();
      }
    });

    // Subscribe to deal data changes to update loan numbers display when editing
    this.dealData$.subscribe((data) => {
      // Always update the local dealData property
      this.dealData = data;

      if (this.isEditable && this.requestData && this.requestData.dealNumber && data && data.length > 0) {
        const selectedDeal = data.find(deal => deal.name === this.requestData.dealNumber);
        if (selectedDeal && selectedDeal.loanNumbers) {
          this.selectedDealLoanNumbers = selectedDeal.loanNumbers;
          this.requestData.selectedDealLoanNumbers = this.selectedDealLoanNumbers;
        }
      }
    });

    // Subscribe to loan data changes and refresh form configuration
    this.loanData$.subscribe((data) => {
      // Always update the local loanData property
      this.loanData = data;

      // Only refresh if we have a current request type and the form is already configured
      // Also check if we're currently updating loan data to prevent immediate reset
      // Skip form update for LOAN ADVANCE as it destroys the data grid
      if (
        this.currentRequestType &&
        this.formItems.length > 0 &&
        this.isUpdatingLoanData &&
        this.currentRequestType !== "LOAN ADVANCE"
      ) {
        // Instead of recreating the entire form, just update the loan data in existing config
        this.updateLoanDataInFormConfig();
      }
    });

  }

  onFileUpload(file: File) {
    this.files.push(file);
  }

  onFileRemove(file: File) {
    if (file != null && file != undefined) {
      this.files = this.files.filter((f) => f !== file);
    }
  }

  override onShowInactiveLoansChanged(e: any): void {
    this.showInactiveLoans = e.value;
    this.isUpdatingLoanData = true;
    super.onShowInactiveLoansChanged(e);

    // For LOAN ADVANCE, don't update the form config as it destroys the data grid
    // The loan data change will be picked up by the lookup in the grid automatically
     if (this.currentRequestType !== "LOAN ADVANCE" && this.currentRequestType !== "AUDIT CONFIRM") {
      // Reset flag after a short delay to allow the subscription to process
      setTimeout(() => {
        this.isUpdatingLoanData = false;
      }, 50);
    } else {
      // For LOAN ADVANCE, just reset the flag immediately without form update
      this.isUpdatingLoanData = false;
    }
  }

  override onShowInactiveDealsChanged(e: any): void {
    this.showInactiveDeals = e.value;
    this.isUpdatingDealData = true;



    super.onShowInactiveDealsChanged(e);

    // For EXTENSION requests, handle the form update differently
    if (this.currentRequestType === "EXTENSION" || this.currentRequestType === "Extension") {

      // Since the subscription updates this.dealData immediately, let's use a Promise-based approach
      this.dealData$.pipe(
        filter((data: any) => data && data.length > 0),
        take(1)
      ).subscribe((data: any) => {
        // Try to update the dropdown directly through the form instance
        if (this.currentFormInstance) {
          const dealNumberEditor = this.currentFormInstance.getEditor('dealNumber');
          if (dealNumberEditor) {
            dealNumberEditor.option('items', data);
            dealNumberEditor.repaint();
          } else {
            // Fallback to form recreation
            this.onRequestTypeSelectionChanged({
              value: this.currentRequestType,
              selectedItem: { name: this.currentRequestType }
            });
          }
        } else {
          // Fallback to form recreation
          this.onRequestTypeSelectionChanged({
            value: this.currentRequestType,
            selectedItem: { name: this.currentRequestType }
          });
        }
        this.isUpdatingDealData = false;
      });
    } else if (this.currentRequestType !== "LOAN ADVANCE" && this.currentRequestType !== "AUDIT CONFIRM") {
      // Reset flag after a short delay to allow the subscription to process
      setTimeout(() => {
        this.isUpdatingDealData = false;
      }, 50);
    } else {
      // For LOAN ADVANCE and AUDIT CONFIRM, just reset the flag immediately without form update
      this.isUpdatingDealData = false;
    }
  }

  onDealNumberSelectionChanged(e: any): void {
    const selectedDealName = e.value;
    if (selectedDealName && this.dealData) {
      const selectedDeal = this.dealData.find(deal => deal.name === selectedDealName);
      if (selectedDeal && selectedDeal.loanNumbers) {
        this.selectedDealLoanNumbers = selectedDeal.loanNumbers;
      } else {
        this.selectedDealLoanNumbers = [];
      }
    } else {
      this.selectedDealLoanNumbers = [];
    }

    // Update the form data
    if (this.requestData) {
      this.requestData.selectedDealLoanNumbers = this.selectedDealLoanNumbers;
    }
  }

  /**
   * Deep clone function that preserves functions and validation rules
   * Unlike JSON.parse(JSON.stringify(...)), this preserves function references
   */
  private deepCloneFormItems(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.deepCloneFormItems(item));
    }

    const cloned: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        // Preserve functions and copy everything else recursively
        if (typeof obj[key] === 'function') {
          cloned[key] = obj[key];
        } else {
          cloned[key] = this.deepCloneFormItems(obj[key]);
        }
      }
    }
    return cloned;
  }

  private updateCheckboxInFormItems(items: any[], disabled: boolean): boolean {
    for (const item of items) {
      // Check if this is the show inactive loans checkbox (by CSS class and editor type)
      if (
        item.editorType === "dxCheckBox" &&
        item.cssClass === "loan-checkbox-center-align"
      ) {
        if (!item.editorOptions) {
          item.editorOptions = {};
        }

        // Preserve the current value from the component property
        const currentValue = this.showInactiveLoans;

        // console.log(
        //   "Found and updating checkbox disabled state in form config from",
        //   item.editorOptions.disabled,
        //   "to:",
        //   disabled,
        //   "preserving value:",
        //   currentValue,
        // );

        // Update the disabled state while preserving the value
        item.editorOptions.disabled = disabled;

        // Make sure the value is properly set in the editor options
        item.editorOptions.value = currentValue;

        return true; // Found and updated
      }

      // Check nested items recursively
      if (item.itemType === "group" && item.items) {
        if (this.updateCheckboxInFormItems(item.items, disabled)) {
          return true;
        }
      }

      if (item.items && Array.isArray(item.items)) {
        if (this.updateCheckboxInFormItems(item.items, disabled)) {
          return true;
        }
      }
    }
    return false; // Not found
  }

  private updateLoanDataInFormConfig(): void {
    if (!this.formItems || this.formItems.length === 0) {
      return;
    }

    // Deep clone the current form items using custom clone that preserves functions
    const updatedFormItems = this.deepCloneFormItems(this.formItems);

    // Find and update loan data in all form configurations
    this.updateLoanDataRecursively(updatedFormItems);

    // Emit the updated configuration
    this.formItemsBehaviourSubject$.next(updatedFormItems);
  }

    private updateDealDataInFormConfig(): void {
    if (!this.formItems || this.formItems.length === 0) {
      return;
    }

    // Deep clone the current form items using custom clone that preserves functions
    const updatedFormItems = this.deepCloneFormItems(this.formItems);

    // Find and update loan data in all form configurations
    this.updateDealDataRecursively(updatedFormItems);

    // Emit the updated configuration
    this.formItemsBehaviourSubject$.next(updatedFormItems);
  }

  private updateLoanDataRecursively(items: any[]): void {
    for (const item of items) {
      // Check if this is a loan select box
      if (
        item.dataField === "loanNumber" &&
        item.editorType === "dxSelectBox"
      ) {
        // Update only the items, preserve all other editorOptions including itemTemplate
        if (item.editorOptions) {
          item.editorOptions.items = this.loanData;
          // Ensure itemTemplate is preserved
          if (!item.editorOptions.itemTemplate) {
            item.editorOptions.itemTemplate = this.loanItemTemplate;
          }
        }
      }

      // Check if this is a checkbox for showing inactive loans
      if (
        item.editorType === "dxCheckBox" &&
        item.cssClass === "loan-checkbox-center-align"
      ) {
        if (item.editorOptions) {
          item.editorOptions.value = this.showInactiveLoans;
          item.editorOptions.text = this.activeLoanCheckboxText;
          // Restore the function reference that was lost during JSON serialization
          item.editorOptions.onValueChanged = this.onShowInactiveLoansChanged;
        }
      }

      // Check if this is a group with items
      if (item.itemType === "group" && item.items) {
        this.updateLoanDataRecursively(item.items);
      }

      // Also check for nested items in other structures
      if (item.items && Array.isArray(item.items)) {
        this.updateLoanDataRecursively(item.items);
      }
    }
  }

  private updateDealDataRecursively(items: any[]): void {
  for (const item of items) {
    // Check if this is a deal select box
    if (
      item.dataField === "dealNumber" &&
      item.editorType === "dxSelectBox"
    ) {
      // Update only the items, preserve all other editorOptions including itemTemplate
      if (item.editorOptions) {
        item.editorOptions.items = this.dealData;
        // Ensure itemTemplate is preserved
        if (!item.editorOptions.itemTemplate) {
          item.editorOptions.itemTemplate = this.dealItemTemplate;
        }
        // Ensure onValueChanged is preserved
        if (!item.editorOptions.onValueChanged) {
          item.editorOptions.onValueChanged = this.onDealNumberSelectionChanged;
        }
      }
    }

      // Check if this is a checkbox for showing inactive deals
      if (
        item.editorType === "dxCheckBox" &&
        item.cssClass === "deal-checkbox-center-align"
      ) {
        if (item.editorOptions) {
          item.editorOptions.value = this.showInactiveDeals;
          item.editorOptions.text = this.activeDealCheckboxText;
          // Restore the function reference that was lost during JSON serialization
          item.editorOptions.onValueChanged = this.onShowInactiveDealsChanged;
        }
      }

      // Check if this is a group with items
      if (item.itemType === "group" && item.items) {
        this.updateDealDataRecursively(item.items);
      }

      // Also check for nested items in other structures
      if (item.items && Array.isArray(item.items)) {
        this.updateDealDataRecursively(item.items);
      }
    }
  }

  private updateCollectionAccountInFormConfig(): void {
    if (!this.formItems || this.formItems.length === 0) {
      return;
    }

    // Deep clone the current form items using custom clone that preserves functions
    const updatedFormItems = this.deepCloneFormItems(this.formItems);

    // Find and update collection account dropdown items recursively
    this.updateCollectionAccountRecursively(updatedFormItems);

    // Emit the updated configuration
    this.formItemsBehaviourSubject$.next(updatedFormItems);
  }

  private updateCollectionAccountRecursively(items: any[]): void {
    for (const item of items) {
      if (item.dataField === 'collectionAccount' && item.editorOptions) {
        // Update the items array with the latest bank account data
        item.editorOptions.items = this.collectionAccountsSubject$.value;
      }

      // Recursively check nested items (for grouped form items)
      if (item.items && Array.isArray(item.items)) {
        this.updateCollectionAccountRecursively(item.items);
      }
    }
  }

  onRequestTypeSelectionChanged(e: any) {
    this.currentRequestType = e.selectedItem.name;
    let config = [];
    switch (e.selectedItem.name.toUpperCase()) {
      case "VENDOR - REVISION TO EXISTING TEMPLATE":
        this.mandatoryFiles = !this.isEditable;
        config = [
          {
            dataField: "requestType",
            editorType: "dxTextBox",
            label: { text: "Request Type" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: { value: e.value, readOnly: true, required: "true" },
            validationRules: [
              { type: "required", message: "Request Type is required" },
            ],
          },
          {
            itemType: "group",
            colCount: 4,
            colSpan: 2,
            items: [
              {
                labelMode: "static",
                dataField: "loanNumber",
                editorType: "dxSelectBox",
                colSpan: 3,
                cssClass: "dx-widget",
                label: { text: "Loan Number" },
                editorOptions: {
                  items: this.loanData,
                  valueExpr: "name",
                  displayExpr: "name",
                  required: true,
                  searchMode: "contains",
                  searchEnabled: true,
                  acceptCustomValue: true,
                  onCustomItemCreating: (e) => ({ name: e.text }),
                  itemTemplate: this.loanItemTemplate,
                },
                validationRules: [
                  { type: "required", message: "Loan Number is required" },
                ],
              },
              {
                editorType: "dxCheckBox",
                label: { visible: false },
                cssClass: "loan-checkbox-center-align",
                editorOptions: {
                  text: this.activeLoanCheckboxText,
                  onValueChanged: this.onShowInactiveLoansChanged,
                  value: this.showInactiveLoans,
                },
              },
            ],
          },
          {
            dataField: "payeeName",
            editorType: "dxTextBox",
            label: { text: "Payee Name" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "" },
            validationRules: [
              { type: "required", message: "Payee Name is required" },
            ],
          },
          {
            dataField: "disbursementType",
            editorType: "dxSelectBox",
            label: { text: "Disbursement Type" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: {
              items: this.disbursementTypes,
              valueExpr: "name",
              displayExpr: "name",
              required: true,
              searchMode: "contains",
              searchEnabled: true,
              value: 'Wire',
            },
            validationRules: [
              { type: "required", message: "Disbursement Type is required" },
            ],
          },
          // {
          //   dataField: "dateNeeded",
          //   editorType: "dxDateBox",
          //   label: { text: "Date Needed" },
          //   colSpan: 2,
          //   cssClass: "dx-widget",
          //   editorOptions: { min: this.fifthBusinessDayFromTodaySubject$.value, value: this.fifthBusinessDayFromTodaySubject$.value, width: "40%" },
          //   validationRules: [
          //     {
          //       type: "required",
          //       message: "Date Needed is required",
          //     },
          //   ],
          // },
          {
            colSpan: 2,
            dataField: "isUrgent",
            editorType: "dxCheckBox",
            label: { text: "Rush Job?" },
            editorOptions: {text: ""},
            onselectionchange: (e: any) => {
              this.isUrgent = e.value;
            }
          },
          {
            dataField: "originalVendorRequestNumber",
            editorType: "dxTextBox",
            label: { text: "Original Vendor Request Number" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "" },
            validationRules: [
              { type: "required", message: "Original Vendor Request Number is required" },
            ],
          },
          {
            colSpan: 2,
            dataField: "description",
            editorType: "dxTextArea",
            label: { text: "Description" },
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "", height: 100 },
            validationRules: [
              { type: "required", message: "Description is required" },
            ],
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            itemType: "simple",
            name: "fileUpload",
            label: {
              text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
            },
            template: "fileUploadTemplate",
          },
          {
            itemType: "simple",
            colSpan: 2,
            label: { visible: false },
            template: (data, container) => {
              // Create a wrapper div
              const wrapper = document.createElement('div');
              wrapper.style.display = 'flex';
              wrapper.style.alignItems = 'center';
              wrapper.style.gap = '8px';
              wrapper.style.marginBottom = '10px';

              // Create label text
              const labelText = document.createElement('span');
              labelText.style.fontWeight = '500';
              labelText.style.fontSize = '14px';
              labelText.textContent = 'Documentation Required';

              // Create info icon
              const icon = document.createElement('i');
              icon.className = 'dx-icon dx-icon-info doc-tooltip-icon';
              icon.style.cursor = 'help';
              icon.style.color = '#337ab7';
              icon.style.fontSize = '16px';

              // Create tooltip div
              const tooltip = document.createElement('div');
              tooltip.id = 'doc-requirements';
              tooltip.className = 'custom-tooltip';
              tooltip.style.display = 'none';
              tooltip.style.position = 'fixed';
              tooltip.style.zIndex = '10000';
              tooltip.style.backgroundColor = '#fff';
              tooltip.style.border = '1px solid #ddd';
              tooltip.style.borderRadius = '4px';
              tooltip.style.padding = '12px';
              tooltip.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              tooltip.style.maxWidth = '400px';

              tooltip.innerHTML = `
                <ol style="margin: 0; padding-left: 20px; font-size: 12px; line-height: 1.5;">
                  <li>Wiring instructions or check payment details</li>
                  <li>W-9 or W-8 for all vendors except governmental agencies (ex. taxing authorities)</li>
                  <li>Contact name and phone number for confirming wiring instructions (can be added in the comments)</li>
                  <li>
                    File Attachments*:
                    <ul style="margin: 0; padding-left: 20px; font-size: 12px; line-height: 1.5;">
                      <li>Borrower Distribution vendors require W-9 or W-8, wiring instructions or check payment details</li>
                      <li>Remittance vendors require W-9 or W-8, wiring instructions</li>
                      <li>Third Party vendor payments require a W-9 or W-8 (not required for governmental agencies including taxing authorities), wiring instructions or check payment details</li>
                      <li>Tax Authority vendors require wiring instructions or check payment details</li>
                      <li>Insurance Agent/Company vendors require W-9 or W-8, wiring instructions or check payment details. Contact information for verifying wiring instructions is required when wire is the disbursement type</li>
                    </ul>
                  </li>
                </ol>
              `;

              // Add event listeners
              icon.addEventListener('mouseenter', () => {
                const iconRect = icon.getBoundingClientRect();
                tooltip.style.display = 'block';

                // Position above the icon
                const tooltipHeight = tooltip.offsetHeight;
                tooltip.style.left = `${iconRect.left}px`;
                tooltip.style.top = `${iconRect.top - tooltipHeight - 5}px`;
              });

              icon.addEventListener('mouseleave', () => {
                // Small delay to allow moving to tooltip
                setTimeout(() => {
                  if (!tooltip.matches(':hover')) {
                    tooltip.style.display = 'none';
                  }
                }, 100);
              });

              tooltip.addEventListener('mouseleave', () => {
                tooltip.style.display = 'none';
              });

              // Append elements
              wrapper.appendChild(labelText);
              wrapper.appendChild(icon);
              container.appendChild(wrapper);

              // Append tooltip to body (not to container) for better positioning
              document.body.appendChild(tooltip);
            }
          },
          {
            // work around to bind file upload component to form and validate it
            dataField: "fileUploadProxy",
            colSpan: 2,
            itemType: "simple",
            label: { visible: false },
            editorType: "dxTextBox",
            editorOptions: {
              visible: false,
              value: this.files.length.toString(),
            },
            validationRules: [
              {
                type: "custom",
                message: "At least one file is required",
                validationCallback: (e) => this.onValidateFileAttachments(e),
                reevaluate: true,
              },
            ],
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            cssClass: "borrow-submit",
            editorType: "dxButton",
            editorOptions: { text: "Submit", useSubmitBehavior: true },
          },
        ];
        break;
      case "REMITTANCE - REVISION TO EXISTING TEMPLATE":
        this.mandatoryFiles = !this.isEditable;
        config = [
          {
            dataField: "requestType",
            editorType: "dxTextBox",
            label: { text: "Request Type" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: { value: e.value, readOnly: true, required: "true" },
            validationRules: [
              { type: "required", message: "Request Type is required" },
            ],
          },
          {
            itemType: "group",
            colCount: 4,
            colSpan: 2,
            items: [
              {
                labelMode: "static",
                dataField: "loanNumber",
                editorType: "dxSelectBox",
                colSpan: 3,
                cssClass: "dx-widget",
                label: { text: "Loan Number" },
                editorOptions: {
                  items: this.loanData,
                  valueExpr: "name",
                  displayExpr: "name",
                  required: true,
                  searchMode: "contains",
                  searchEnabled: true,
                  acceptCustomValue: true,
                  onCustomItemCreating: (e) => ({ name: e.text }),
                  itemTemplate: this.loanItemTemplate,
                },
                validationRules: [
                  { type: "required", message: "Loan Number is required" },
                ],
              },
              {
                editorType: "dxCheckBox",
                label: { visible: false },
                cssClass: "loan-checkbox-center-align",
                editorOptions: {
                  text: this.activeLoanCheckboxText,
                  onValueChanged: this.onShowInactiveLoansChanged,
                  value: this.showInactiveLoans,
                },
              },
            ],
          },
          {
            dataField: "payeeName",
            editorType: "dxTextBox",
            label: { text: "Payee Name" },
            colSpan: 1,
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "" },
            validationRules: [
              { type: "required", message: "Payee Name is required" },
            ],
          },
          {
            dataField: "disbursementType",
            editorType: "dxSelectBox",
            label: { text: "Disbursement Type" },
            colSpan: 1,
            cssClass: "dx-widget",
            editorOptions: {
              items: this.disbursementTypes,
              valueExpr: "name",
              displayExpr: "name",
              required: true,
              searchMode: "contains",
              searchEnabled: true,
              value: 'Wire',
            },
            validationRules: [
              { type: "required", message: "Disbursement Type is required" },
            ],
          },
          {
            dataField: "originalVendorRequestNumber",
            editorType: "dxTextBox",
            label: { text: "Original Vendor Request Number" },
            colSpan: 1,
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "" },
            validationRules: [
              { type: "required", message: "Original Vendor Request Number is required" },
            ],
          },
          // {
          //   dataField: "dateNeeded",
          //   editorType: "dxDateBox",
          //   label: { text: "Date Needed" },
          //   colSpan: 2,
          //   cssClass: "dx-widget",
          //   editorOptions: { min: this.fifthBusinessDayFromTodaySubject$.value, value: this.fifthBusinessDayFromTodaySubject$.value, width: "40%" },
          //   validationRules: [
          //     {
          //       type: "required",
          //       message: "Date Needed is required",
          //     },
          //   ],
          // },
          {
            colSpan: 1,
            dataField: "isUrgent",
            editorType: "dxCheckBox",
            label: { text: "Rush Job?" },
            editorOptions: {text: ""},
            onselectionchange: (e: any) => {
              this.isUrgent = e.value;
            }
          },
          {
            dataField: "collectionAccount",
            editorType: "dxSelectBox",
            label: { text: "Collection Account" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: {
              items: this.collectionAccountsSubject$.value,
              valueExpr: "name",
              displayExpr: "name",
              required: true,
              searchMode: "contains",
              searchEnabled: true,
              acceptCustomValue: true,
              onCustomItemCreating: (e) => e.text,
              onInitialized: (e) => {
                // Get the root element of the widget
                const element = e.component.element();
                // Set the data-testid attribute on the root element
                element.setAttribute('data-testid', 'collection-account-select');
              }
            },

            validationRules: [
              { type: "required", message: "Collection Account is required" },
            ],
          },
          {
            colSpan: 2,
            dataField: "description",
            editorType: "dxTextArea",
            label: { text: "Description" },
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "", height: 100 },
            validationRules: [
              { type: "required", message: "Description is required" },
            ],
          },
          {
            itemType: "simple",
            colSpan: 2,
            label: { visible: false },
            template: (data, container) => {
              // Create a wrapper div
              const wrapper = document.createElement('div');
              wrapper.style.display = 'flex';
              wrapper.style.alignItems = 'center';
              wrapper.style.gap = '8px';
              wrapper.style.marginBottom = '10px';

              // Create label text
              const labelText = document.createElement('span');
              labelText.style.fontWeight = '500';
              labelText.style.fontSize = '14px';
              labelText.textContent = 'Documentation Required';

              // Create info icon
              const icon = document.createElement('i');
              icon.className = 'dx-icon dx-icon-info doc-tooltip-icon';
              icon.style.cursor = 'help';
              icon.style.color = '#337ab7';
              icon.style.fontSize = '16px';

              // Create tooltip div
              const tooltip = document.createElement('div');
              tooltip.id = 'doc-requirements';
              tooltip.className = 'custom-tooltip';
              tooltip.style.display = 'none';
              tooltip.style.position = 'fixed';
              tooltip.style.zIndex = '10000';
              tooltip.style.backgroundColor = '#fff';
              tooltip.style.border = '1px solid #ddd';
              tooltip.style.borderRadius = '4px';
              tooltip.style.padding = '12px';
              tooltip.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              tooltip.style.maxWidth = '400px';

              tooltip.innerHTML = `
                <ol style="margin: 0; padding-left: 20px; font-size: 12px; line-height: 1.5;">
                  <li>Wiring instructions or check payment details</li>
                  <li>W-9 or W-8 for all vendors except governmental agencies (ex. taxing authorities)</li>
                  <li>Contact name and phone number for confirming wiring instructions (can be added in the comments)</li>
                  <li>
                    File Attachments*:
                    <ul style="margin: 0; padding-left: 20px; font-size: 12px; line-height: 1.5;">
                      <li>Borrower Distribution vendors require W-9 or W-8, wiring instructions or check payment details</li>
                      <li>Remittance vendors require W-9 or W-8, wiring instructions</li>
                      <li>Third Party vendor payments require a W-9 or W-8 (not required for governmental agencies including taxing authorities), wiring instructions or check payment details</li>
                      <li>Tax Authority vendors require wiring instructions or check payment details</li>
                      <li>Insurance Agent/Company vendors require W-9 or W-8, wiring instructions or check payment details. Contact information for verifying wiring instructions is required when wire is the disbursement type</li>
                    </ul>
                  </li>
                </ol>
              `;

              // Add event listeners
              icon.addEventListener('mouseenter', () => {
                const iconRect = icon.getBoundingClientRect();
                tooltip.style.display = 'block';

                // Position above the icon
                const tooltipHeight = tooltip.offsetHeight;
                tooltip.style.left = `${iconRect.left}px`;
                tooltip.style.top = `${iconRect.top - tooltipHeight - 5}px`;
              });

              icon.addEventListener('mouseleave', () => {
                // Small delay to allow moving to tooltip
                setTimeout(() => {
                  if (!tooltip.matches(':hover')) {
                    tooltip.style.display = 'none';
                  }
                }, 100);
              });

              tooltip.addEventListener('mouseleave', () => {
                tooltip.style.display = 'none';
              });

              // Append elements
              wrapper.appendChild(labelText);
              wrapper.appendChild(icon);
              container.appendChild(wrapper);

              // Append tooltip to body (not to container) for better positioning
              document.body.appendChild(tooltip);
            }
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            itemType: "simple",
            name: "fileUpload",
            label: {
              text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
            },
            template: "fileUploadTemplate",
          },
          {
            // work around to bind file upload component to form and validate it
            dataField: "fileUploadProxy",
            colSpan: 2,
            itemType: "simple",
            label: { visible: false },
            editorType: "dxTextBox",
            editorOptions: {
              visible: false,
              value: this.files.length.toString(),
            },
            validationRules: [
              {
                type: "custom",
                message: "At least one file is required",
                validationCallback: (e) => this.onValidateFileAttachments(e),
                reevaluate: true,
              },
            ],
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            cssClass: "borrow-submit",
            editorType: "dxButton",
            editorOptions: { text: "Submit", useSubmitBehavior: true },
          },
        ];
        break;
      case "REMITTANCE - ASSET LEVEL":
        this.mandatoryFiles = !this.isEditable;
        config = [
          {
            dataField: "requestType",
            editorType: "dxTextBox",
            label: { text: "Request Type" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: { value: e.value, readOnly: true, required: "true" },
            validationRules: [
              { type: "required", message: "Request Type is required" },
            ],
          },
          {
            itemType: "group",
            colCount: 4,
            colSpan: 2,
            items: [
              {
                labelMode: "static",
                dataField: "loanNumber",
                editorType: "dxSelectBox",
                colSpan: 3,
                cssClass: "dx-widget",
                label: { text: "Loan Number" },
                editorOptions: {
                  items: this.loanData,
                  valueExpr: "name",
                  displayExpr: "name",
                  required: true,
                  searchMode: "contains",
                  searchEnabled: true,
                  acceptCustomValue: true,
                  onCustomItemCreating: (e) => ({ name: e.text }),
                  itemTemplate: this.loanItemTemplate,
                },
                validationRules: [
                  { type: "required", message: "Loan Number is required" },
                ],
              },
              {
                editorType: "dxCheckBox",
                label: { visible: false },
                cssClass: "loan-checkbox-center-align",
                editorOptions: {
                  text: this.activeLoanCheckboxText,
                  onValueChanged: this.onShowInactiveLoansChanged,
                  value: this.showInactiveLoans,
                },
              },
            ],
          },
          {
            dataField: "payeeName",
            editorType: "dxTextBox",
            label: { text: "Payee Name" },
            colSpan: 1,
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "" },
            validationRules: [
              { type: "required", message: "Payee Name is required" },
            ],
          },
          {
            dataField: "disbursementType",
            editorType: "dxSelectBox",
            label: { text: "Disbursement Type" },
            colSpan: 1,
            cssClass: "dx-widget",
            editorOptions: {
              items: this.disbursementTypes,
              valueExpr: "name",
              displayExpr: "name",
              required: true,
              searchMode: "contains",
              searchEnabled: true,
              value: 'Wire',
            },
            validationRules: [
              { type: "required", message: "Disbursement Type is required" },
            ],
          },
          // {
          //   dataField: "dateNeeded",
          //   editorType: "dxDateBox",
          //   label: { text: "Date Needed" },
          //   colSpan: 2,
          //   cssClass: "dx-widget",
          //   editorOptions: { min: this.fifthBusinessDayFromTodaySubject$.value, value: this.fifthBusinessDayFromTodaySubject$.value, width: "40%" },
          //   validationRules: [
          //     {
          //       type: "required",
          //       message: "Date Needed is required",
          //     },
          //   ],
          // },
          {
            colSpan: 2,
            dataField: "isUrgent",
            editorType: "dxCheckBox",
            label: { text: "Rush Job?" },
            editorOptions: {text: ""},
            onselectionchange: (e: any) => {
              this.isUrgent = e.value;
            }
          },
          {
            dataField: "collectionAccount",
            editorType: "dxSelectBox",
            label: { text: "Collection Account" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: {
              items: this.collectionAccountsSubject$.value,
              valueExpr: "name",
              displayExpr: "name",
              required: true,
              searchMode: "contains",
              searchEnabled: true,
              acceptCustomValue: true,
              onCustomItemCreating: (e) => e.text,
              onInitialized: (e) => {
                // Get the root element of the widget
                const element = e.component.element();
                // Set the data-testid attribute on the root element
                element.setAttribute('data-testid', 'collection-account-select');
              }
            },
            validationRules: [
              { type: "required", message: "Collection Account is required" },
            ],
          },
          {
            colSpan: 2,
            dataField: "description",
            editorType: "dxTextArea",
            label: { text: "Description" },
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "", height: 100 },
            validationRules: [
              { type: "required", message: "Description is required" },
            ],
          },
          {
            itemType: "simple",
            colSpan: 2,
            label: { visible: false },
            template: (data, container) => {
              // Create a wrapper div
              const wrapper = document.createElement('div');
              wrapper.style.display = 'flex';
              wrapper.style.alignItems = 'center';
              wrapper.style.gap = '8px';
              wrapper.style.marginBottom = '10px';

              // Create label text
              const labelText = document.createElement('span');
              labelText.style.fontWeight = '500';
              labelText.style.fontSize = '14px';
              labelText.textContent = 'Documentation Required';

              // Create info icon
              const icon = document.createElement('i');
              icon.className = 'dx-icon dx-icon-info doc-tooltip-icon';
              icon.style.cursor = 'help';
              icon.style.color = '#337ab7';
              icon.style.fontSize = '16px';

              // Create tooltip div
              const tooltip = document.createElement('div');
              tooltip.id = 'doc-requirements';
              tooltip.className = 'custom-tooltip';
              tooltip.style.display = 'none';
              tooltip.style.position = 'fixed';
              tooltip.style.zIndex = '10000';
              tooltip.style.backgroundColor = '#fff';
              tooltip.style.border = '1px solid #ddd';
              tooltip.style.borderRadius = '4px';
              tooltip.style.padding = '12px';
              tooltip.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              tooltip.style.maxWidth = '400px';

              tooltip.innerHTML = `
                <ol style="margin: 0; padding-left: 20px; font-size: 12px; line-height: 1.5;">
                  <li>Wiring instructions or check payment details</li>
                  <li>W-9 or W-8 for all vendors except governmental agencies (ex. taxing authorities)</li>
                  <li>Contact name and phone number for confirming wiring instructions (can be added in the comments)</li>
                  <li>
                    File Attachments*:
                    <ul style="margin: 0; padding-left: 20px; font-size: 12px; line-height: 1.5;">
                      <li>Borrower Distribution vendors require W-9 or W-8, wiring instructions or check payment details</li>
                      <li>Remittance vendors require W-9 or W-8, wiring instructions</li>
                      <li>Third Party vendor payments require a W-9 or W-8 (not required for governmental agencies including taxing authorities), wiring instructions or check payment details</li>
                      <li>Tax Authority vendors require wiring instructions or check payment details</li>
                      <li>Insurance Agent/Company vendors require W-9 or W-8, wiring instructions or check payment details. Contact information for verifying wiring instructions is required when wire is the disbursement type</li>
                    </ul>
                  </li>
                </ol>
              `;

              // Add event listeners
              icon.addEventListener('mouseenter', () => {
                const iconRect = icon.getBoundingClientRect();
                tooltip.style.display = 'block';

                // Position above the icon
                const tooltipHeight = tooltip.offsetHeight;
                tooltip.style.left = `${iconRect.left}px`;
                tooltip.style.top = `${iconRect.top - tooltipHeight - 5}px`;
              });

              icon.addEventListener('mouseleave', () => {
                // Small delay to allow moving to tooltip
                setTimeout(() => {
                  if (!tooltip.matches(':hover')) {
                    tooltip.style.display = 'none';
                  }
                }, 100);
              });

              tooltip.addEventListener('mouseleave', () => {
                tooltip.style.display = 'none';
              });

              // Append elements
              wrapper.appendChild(labelText);
              wrapper.appendChild(icon);
              container.appendChild(wrapper);

              // Append tooltip to body (not to container) for better positioning
              document.body.appendChild(tooltip);
            }
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            itemType: "simple",
            name: "fileUpload",
            label: {
              text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
            },
            template: "fileUploadTemplate",
          },
          {
            // work around to bind file upload component to form and validate it
            dataField: "fileUploadProxy",
            colSpan: 2,
            itemType: "simple",
            label: { visible: false },
            editorType: "dxTextBox",
            editorOptions: {
              visible: false,
              value: this.files.length.toString(),
            },
            validationRules: [
              {
                type: "custom",
                message: "At least one file is required",
                validationCallback: (e) => this.onValidateFileAttachments(e),
                reevaluate: true,
              },
            ],
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            cssClass: "borrow-submit",
            editorType: "dxButton",
            editorOptions: { text: "Submit", useSubmitBehavior: true },
          },
        ];
        break;
      case "REMITTANCE - PORTFOLIO LEVEL":
      case "REMITTANCE - SYNDICATION":
        this.mandatoryFiles = !this.isEditable;
        config = [
          {
            dataField: "requestType",
            editorType: "dxTextBox",
            label: { text: "Request Type" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: { value: e.value, readOnly: true, required: "true" },
            validationRules: [
              { type: "required", message: "Request Type is required" },
            ],
          },
          {
            itemType: "group",
            colCount: 4,
            colSpan: 2,
            items: [
              {
                labelMode: "static",
                dataField: "loanNumber",
                editorType: "dxSelectBox",
                colSpan: 3,
                cssClass: "dx-widget",
                label: { text: "Loan Number" },
                editorOptions: {
                  items: this.loanData,
                  valueExpr: "name",
                  displayExpr: "name",
                  required: true,
                  searchMode: "contains",
                  searchEnabled: true,
                  acceptCustomValue: true,
                  onCustomItemCreating: (e) => ({ name: e.text }),
                  itemTemplate: this.loanItemTemplate,
                },
                validationRules: [
                  { type: "required", message: "Loan Number is required" },
                ],
              },
              {
                editorType: "dxCheckBox",
                label: { visible: false },
                cssClass: "loan-checkbox-center-align",
                editorOptions: {
                  text: this.activeLoanCheckboxText,
                  onValueChanged: this.onShowInactiveLoansChanged,
                  value: this.showInactiveLoans,
                },
              },
            ],
          },
          {
            dataField: "payeeName",
            editorType: "dxTextBox",
            label: { text: "Payee Name" },
            colSpan: 1,
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "" },
            validationRules: [
              { type: "required", message: "Payee Name is required" },
            ],
          },
          {
            dataField: "disbursementType",
            editorType: "dxSelectBox",
            label: { text: "Disbursement Type" },
            colSpan: 1,
            cssClass: "dx-widget",
            editorOptions: {
              items: this.disbursementTypes,
              valueExpr: "name",
              displayExpr: "name",
              required: true,
              searchMode: "contains",
              searchEnabled: true,
              value: 'Wire',
            },
            validationRules: [
              { type: "required", message: "Disbursement Type is required" },
            ],
          },
          // {
          //   dataField: "dateNeeded",
          //   editorType: "dxDateBox",
          //   label: { text: "Date Needed" },
          //   colSpan: 2,
          //   cssClass: "dx-widget",
          //   editorOptions: { min: this.fifthBusinessDayFromTodaySubject$.value, value: this.fifthBusinessDayFromTodaySubject$.value, width: "40%" },
          //   validationRules: [
          //     {
          //       type: "required",
          //       message: "Date Needed is required",
          //     },
          //   ],
          // },
          {
            colSpan: 2,
            dataField: "isUrgent",
            editorType: "dxCheckBox",
            label: { text: "Rush Job?" },
            editorOptions: {text: ""},
            onselectionchange: (e: any) => {
              this.isUrgent = e.value;
            }
          },
          {
            dataField: "collectionAccount",
            editorType: "dxSelectBox",
            label: { text: "Collection Account" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: {
              items: this.collectionAccountsSubject$.value,
              valueExpr: "name",
              displayExpr: "name",
              required: true,
              searchMode: "contains",
              searchEnabled: true,
              acceptCustomValue: true,
              onCustomItemCreating: (e) => e.text,
              onInitialized: (e) => {
                // Get the root element of the widget
                const element = e.component.element();
                // Set the data-testid attribute on the root element
                element.setAttribute('data-testid', 'collection-account-select');
              }
            },
            validationRules: [
              { type: "required", message: "Collection Account is required" },
            ],
          },
          {
            colSpan: 2,
            dataField: "description",
            editorType: "dxTextArea",
            label: { text: "Description" },
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "", height: 100 },
            validationRules: [
              { type: "required", message: "Description is required" },
            ],
          },
          {
            itemType: "simple",
            colSpan: 2,
            label: { visible: false },
            template: (data, container) => {
              // Create a wrapper div
              const wrapper = document.createElement('div');
              wrapper.style.display = 'flex';
              wrapper.style.alignItems = 'center';
              wrapper.style.gap = '8px';
              wrapper.style.marginBottom = '10px';

              // Create label text
              const labelText = document.createElement('span');
              labelText.style.fontWeight = '500';
              labelText.style.fontSize = '14px';
              labelText.textContent = 'Documentation Required';

              // Create info icon
              const icon = document.createElement('i');
              icon.className = 'dx-icon dx-icon-info doc-tooltip-icon';
              icon.style.cursor = 'help';
              icon.style.color = '#337ab7';
              icon.style.fontSize = '16px';

              // Create tooltip div
              const tooltip = document.createElement('div');
              tooltip.id = 'doc-requirements';
              tooltip.className = 'custom-tooltip';
              tooltip.style.display = 'none';
              tooltip.style.position = 'fixed';
              tooltip.style.zIndex = '10000';
              tooltip.style.backgroundColor = '#fff';
              tooltip.style.border = '1px solid #ddd';
              tooltip.style.borderRadius = '4px';
              tooltip.style.padding = '12px';
              tooltip.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              tooltip.style.maxWidth = '400px';

              tooltip.innerHTML = `
                <ol style="margin: 0; padding-left: 20px; font-size: 12px; line-height: 1.5;">
                  <li>Wiring instructions or check payment details</li>
                  <li>W-9 or W-8 for all vendors except governmental agencies (ex. taxing authorities)</li>
                  <li>Contact name and phone number for confirming wiring instructions (can be added in the comments)</li>
                  <li>
                    File Attachments*:
                    <ul style="margin: 0; padding-left: 20px; font-size: 12px; line-height: 1.5;">
                      <li>Borrower Distribution vendors require W-9 or W-8, wiring instructions or check payment details</li>
                      <li>Remittance vendors require W-9 or W-8, wiring instructions</li>
                      <li>Third Party vendor payments require a W-9 or W-8 (not required for governmental agencies including taxing authorities), wiring instructions or check payment details</li>
                      <li>Tax Authority vendors require wiring instructions or check payment details</li>
                      <li>Insurance Agent/Company vendors require W-9 or W-8, wiring instructions or check payment details. Contact information for verifying wiring instructions is required when wire is the disbursement type</li>
                    </ul>
                  </li>
                </ol>
              `;

              // Add event listeners
              icon.addEventListener('mouseenter', () => {
                const iconRect = icon.getBoundingClientRect();
                tooltip.style.display = 'block';

                // Position above the icon
                const tooltipHeight = tooltip.offsetHeight;
                tooltip.style.left = `${iconRect.left}px`;
                tooltip.style.top = `${iconRect.top - tooltipHeight - 5}px`;
              });

              icon.addEventListener('mouseleave', () => {
                // Small delay to allow moving to tooltip
                setTimeout(() => {
                  if (!tooltip.matches(':hover')) {
                    tooltip.style.display = 'none';
                  }
                }, 100);
              });

              tooltip.addEventListener('mouseleave', () => {
                tooltip.style.display = 'none';
              });

              // Append elements
              wrapper.appendChild(labelText);
              wrapper.appendChild(icon);
              container.appendChild(wrapper);

              // Append tooltip to body (not to container) for better positioning
              document.body.appendChild(tooltip);
            }
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            itemType: "simple",
            name: "fileUpload",
            label: {
              text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
            },
            template: "fileUploadTemplate",
          },
          {
            // work around to bind file upload component to form and validate it
            dataField: "fileUploadProxy",
            colSpan: 2,
            itemType: "simple",
            label: { visible: false },
            editorType: "dxTextBox",
            editorOptions: {
              visible: false,
              value: this.files.length.toString(),
            },
            validationRules: [
              {
                type: "custom",
                message: "At least one file is required",
                validationCallback: (e) => this.onValidateFileAttachments(e),
                reevaluate: true,
              },
            ],
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            cssClass: "borrow-submit",
            editorType: "dxButton",
            editorOptions: { text: "Submit", useSubmitBehavior: true },
          },
        ];
        break;
      default:
        this.mandatoryFiles = !this.isEditable;
        config = [
          {
            dataField: "requestType",
            editorType: "dxTextBox",
            label: { text: "Request Type" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: { value: e.value, readOnly: true, required: "true" },
            validationRules: [
              { type: "required", message: "Request Type is required" },
            ],
          },
          {
            itemType: "group",
            colCount: 4,
            colSpan: 2,
            items: [
              {
                labelMode: "static",
                dataField: "loanNumber",
                editorType: "dxSelectBox",
                colSpan: 3,
                cssClass: "dx-widget",
                label: { text: "Loan Number" },
                editorOptions: {
                  items: this.loanData,
                  valueExpr: "name",
                  displayExpr: "name",
                  required: true,
                  searchMode: "contains",
                  searchEnabled: true,
                  acceptCustomValue: true,
                  onCustomItemCreating: (e) => ({ name: e.text }),
                  itemTemplate: this.loanItemTemplate,
                },
                validationRules: [
                  { type: "required", message: "Loan Number is required" },
                ],
              },
              {
                editorType: "dxCheckBox",
                label: { visible: false },
                cssClass: "loan-checkbox-center-align",
                editorOptions: {
                  text: this.activeLoanCheckboxText,
                  onValueChanged: this.onShowInactiveLoansChanged,
                  value: this.showInactiveLoans,
                },
              },
            ],
          },
          {
            dataField: "payeeName",
            editorType: "dxTextBox",
            label: { text: "Payee Name" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "" },
            validationRules: [
              { type: "required", message: "Payee Name is required" },
            ],
          },
          {
            dataField: "disbursementType",
            editorType: "dxSelectBox",
            label: { text: "Disbursement Type" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: {
              items: this.disbursementTypes,
              valueExpr: "name",
              displayExpr: "name",
              required: true,
              searchMode: "contains",
              searchEnabled: true,
              value: 'Wire',
            },
            validationRules: [
              { type: "required", message: "Disbursement Type is required" },
            ],
          },
          // {
          //   dataField: "dateNeeded",
          //   editorType: "dxDateBox",
          //   label: { text: "Date Needed" },
          //   colSpan: 2,
          //   cssClass: "dx-widget",
          //   editorOptions: { min: this.fifthBusinessDayFromTodaySubject$.value, value: this.fifthBusinessDayFromTodaySubject$.value, width: "40%" },
          //   validationRules: [
          //     {
          //       type: "required",
          //       message: "Date Needed is required",
          //     },
          //   ],
          // },
          {
            colSpan: 2,
            dataField: "isUrgent",
            editorType: "dxCheckBox",
            label: { text: "Rush Job?" },
            editorOptions: {text: ""},
            onselectionchange: (e: any) => {
              this.isUrgent = e.value;
            }
          },
          {
            colSpan: 2,
            dataField: "description",
            editorType: "dxTextArea",
            label: { text: "Description" },
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "", height: 100 },
            validationRules: [
              { type: "required", message: "Description is required" },
            ],
          },
          {
            itemType: "simple",
            colSpan: 2,
            label: { visible: false },
            template: (data, container) => {
              // Create a wrapper div
              const wrapper = document.createElement('div');
              wrapper.style.display = 'flex';
              wrapper.style.alignItems = 'center';
              wrapper.style.gap = '8px';
              wrapper.style.marginBottom = '10px';

              // Create label text
              const labelText = document.createElement('span');
              labelText.style.fontWeight = '500';
              labelText.style.fontSize = '14px';
              labelText.textContent = 'Documentation Required';

              // Create info icon
              const icon = document.createElement('i');
              icon.className = 'dx-icon dx-icon-info doc-tooltip-icon';
              icon.style.cursor = 'help';
              icon.style.color = '#337ab7';
              icon.style.fontSize = '16px';

              // Create tooltip div
              const tooltip = document.createElement('div');
              tooltip.id = 'doc-requirements';
              tooltip.className = 'custom-tooltip';
              tooltip.style.display = 'none';
              tooltip.style.position = 'fixed';
              tooltip.style.zIndex = '10000';
              tooltip.style.backgroundColor = '#fff';
              tooltip.style.border = '1px solid #ddd';
              tooltip.style.borderRadius = '4px';
              tooltip.style.padding = '12px';
              tooltip.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              tooltip.style.maxWidth = '400px';

              tooltip.innerHTML = `
                <ol style="margin: 0; padding-left: 20px; font-size: 12px; line-height: 1.5;">
                  <li>Wiring instructions or check payment details</li>
                  <li>W-9 or W-8 for all vendors except governmental agencies (ex. taxing authorities)</li>
                  <li>Contact name and phone number for confirming wiring instructions (can be added in the comments)</li>
                  <li>
                    File Attachments*:
                    <ul style="margin: 0; padding-left: 20px; font-size: 12px; line-height: 1.5;">
                      <li>Borrower Distribution vendors require W-9 or W-8, wiring instructions or check payment details</li>
                      <li>Remittance vendors require W-9 or W-8, wiring instructions</li>
                      <li>Third Party vendor payments require a W-9 or W-8 (not required for governmental agencies including taxing authorities), wiring instructions or check payment details</li>
                      <li>Tax Authority vendors require wiring instructions or check payment details</li>
                      <li>Insurance Agent/Company vendors require W-9 or W-8, wiring instructions or check payment details. Contact information for verifying wiring instructions is required when wire is the disbursement type</li>
                    </ul>
                  </li>
                </ol>
              `;

              // Add event listeners
              icon.addEventListener('mouseenter', () => {
                const iconRect = icon.getBoundingClientRect();
                tooltip.style.display = 'block';

                // Position above the icon
                const tooltipHeight = tooltip.offsetHeight;
                tooltip.style.left = `${iconRect.left}px`;
                tooltip.style.top = `${iconRect.top - tooltipHeight - 5}px`;
              });

              icon.addEventListener('mouseleave', () => {
                // Small delay to allow moving to tooltip
                setTimeout(() => {
                  if (!tooltip.matches(':hover')) {
                    tooltip.style.display = 'none';
                  }
                }, 100);
              });

              tooltip.addEventListener('mouseleave', () => {
                tooltip.style.display = 'none';
              });

              // Append elements
              wrapper.appendChild(labelText);
              wrapper.appendChild(icon);
              container.appendChild(wrapper);

              // Append tooltip to body (not to container) for better positioning
              document.body.appendChild(tooltip);
            }
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            itemType: "simple",
            name: "fileUpload",
            label: {
              text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
            },
            template: "fileUploadTemplate",
          },
          {
            // work around to bind file upload component to form and validate it
            dataField: "fileUploadProxy",
            colSpan: 2,
            itemType: "simple",
            label: { visible: false },
            editorType: "dxTextBox",
            editorOptions: {
              visible: false,
              value: this.files.length.toString(),
            },
            validationRules: [
              {
                type: "custom",
                message: "At least one file is required",
                validationCallback: (e) => this.onValidateFileAttachments(e),
                reevaluate: true,
              },
            ],
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            cssClass: "borrow-submit",
            editorType: "dxButton",
            editorOptions: { text: "Submit", useSubmitBehavior: true },
          },
        ];
    }

    // console.log(JSON.stringify(config));
    this.formItemsBehaviourSubject$.next(config);
  }

  // Add this method to generate unique IDs
  getLoanData() {
    if (this.requestData.loanAmounts && this.requestData.loanAmounts.length > 0) {
      // Search in allLoanData to find loan info regardless of active/inactive status
      const firstLoan = this.allLoanData.find((ld) => ld.name === this.requestData.loanAmounts[0].loanNumber);

      // If firstLoan is not found, return all loan data (fallback)
      if (!firstLoan) {
        console.warn('First loan not found in loan data, returning all loans');
        return this.loanData;
      }

      const existingLoanNumbers = this.requestData.loanAmounts.map((item) => item.loanNumber);
      // Filter from current loanData (respects active/inactive checkbox) but use dealName from allLoanData
      const filteredLoans = this.loanData.filter((l) => l.dealName === firstLoan.dealName);

      if (this.doNotExcludeSelectedLoans) {
        //first pass if editing
        this.doNotExcludeSelectedLoans = false;
        return filteredLoans;
      }

      return filteredLoans.filter((l) => !existingLoanNumbers.includes(l.name));
    }

    // If no loan amounts but we have selectedDealLoanNumbers, filter by those
    if (this.selectedDealLoanNumbers && this.selectedDealLoanNumbers.length > 0) {
      return this.loanData.filter((l) => this.selectedDealLoanNumbers.includes(l.name));
    }

    return this.loanData;
  }

  onValidateFileAttachments(e: any): boolean {
    if (this.mandatoryFiles && this.files.length == 0) {
      e.isValid = false;
      return false;
    }
    e.isValid = true;
    return true;
  }

  onFormContentReady(e: any): void {
    this.currentFormInstance = e.component;
  }  removeProxyData(data: string): string {
    let json = JSON.parse(data);
    delete json["fileUploadProxy"];
    delete json["loanAmountsProxy"];
    return JSON.stringify(json);
  }

  async onSubmit(e: any) {
    e.preventDefault();

    let validationResult = validationEngine.validateGroup("formGroup");

    if (validationResult.isValid) {
      try {
        let request: ITaskRequest = new TaskRequest();
        let requestType = this.requestTypes.find((t) => t.name == this.requestData.requestType);
        request.templateId = requestType.id;

        request.name = requestType.name;
        if (this.requestData.loanAmounts != null && this.requestData.loanAmounts.length > 0) {
          request.referenceName = `${this.requestData.loanAmounts[0].loanNumber}`;
        } else {
          request.referenceName = this.requestData.loanNumber || this.requestData.dealNumber || "";
          this.requestData.loanNumber = this.requestData.loanNumber || "";
        }

        request.referenceType = requestType.referenceType;

        // Create a copy of requestData to modify for submission
        let submissionData = { ...this.requestData };

        // For AUDIT CONFIRM requests, rename loanAmounts to loanNumbers
        if (requestType.name.toUpperCase() === "AUDIT CONFIRM" && submissionData.loanAmounts) {
          submissionData.loanNumbers = submissionData.loanAmounts.map(la => la.loanNumber);
          delete submissionData.loanAmounts;
        }

        // collectionAccount is already a string, no formatting needed

        submissionData = this.appendDealNameToSubmission(submissionData);
        submissionData.wireDisbursement = submissionData.disbursementType == "Wire";
        let formData: FormData = new FormData();
        formData.append("templateId", request.templateId);
        formData.append("locations", request.locations);
        formData.append("name", request.name);
        formData.append("referenceName", request.referenceName);
        formData.append(
          "requestJsonData",
          this.removeProxyData(JSON.stringify(submissionData)),
        );
        formData.append("referenceType", request.referenceType);
        formData.append("isUrgent", this.requestData.isUrgent ? "true" : "false");
        formData.append("wireDisbursement", submissionData.disbursementType == "Wire" ? "true" : "false");

        if (this.isEditable) formData.append("parentId", this.task.parentId);

        this.files.forEach((f) => {
          formData.append("files", f, f.name);
        });

        this.isLoading = true;

        const response = await lastValueFrom(
          this.taskDataService.insertTaskAsFormData(formData),
        );
        sendNotificationMessage(
          `${response[1].data[0].task.taskId} task created`,
          this.messageService,
        );

        this.isLoading = false;

        this.formElement.nativeElement.reset();
        this.formElement.nativeElement.submit();
      } catch (err: any) {
        this.isLoading = false;
        this.loggingService.logError(err, SeverityLevel.Error);
        notify(err.message, "error", getNotificationTimeout());
      }
    }
  }

  override async ngOnInit() {
    try {
      this.isLoading = true;
      await super.ngOnInit();
      let requestType = await lastValueFrom(
        this.taskDataService.getTaskTemplatesByType(this.taskTemplateName),
      );
      this.requestTypes = requestType[1];

      // Load bank accounts asynchronously - the BehaviorSubject will notify subscribers when data arrives
      this.treasuryDataService.getBankAccounts().subscribe({
        next: (accountData) => {
          const collectionAccounts = accountData.filter((acc: IAccount) => acc.isCollectionAccount);
          this.collectionAccountsSubject$.next(collectionAccounts.map(acc => ({ name: `${acc.accountNumber} - ${acc.name}`, id: acc.id })));
        },
        error: (err) => {
          this.loggingService.logError(err, SeverityLevel.Error);
          notify("Failed to load bank accounts", "error", getNotificationTimeout());
        }
      });

      const feeTypeData = await lastValueFrom(this.taskDataService.getFeeTypes());
      // Sort and format fee types in title case
      this.feeTypes = feeTypeData[1]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(feeType => ({
          ...feeType,
          name: FormUtils.toTitleCase(feeType.name)
        }));

      this.isEditable = this.task != null ? true : false;

      if (this.isEditable) {
        this.requestData = FormUtils.mapFromTask<any>(this.task, function () {
          return {};
        } as any);
      } else {
        // Initialize disbursementType to 'Wire' for new forms
        this.requestData.disbursementType = 'Wire';
      }

      if (this.isEditable) {

        // console.log(this.requestData);

        // Initialize loan numbers display for deal-based request types
        if (this.requestData.dealNumber && this.dealData) {
          const selectedDeal = this.dealData.find(deal => deal.name === this.requestData.dealNumber);
          if (selectedDeal && selectedDeal.loanNumbers) {
            this.selectedDealLoanNumbers = selectedDeal.loanNumbers;
            this.requestData.selectedDealLoanNumbers = this.selectedDealLoanNumbers;
          }
        }

        //remap loan numbers to add name and dealName for lookup match

        if (
          this.requestData.loanAmounts &&
          this.requestData.loanAmounts.length > 0
        ) {
          const remappedAmounts = [];

          this.requestData.loanAmounts.forEach((amount) => {
            let loan = this.loanData.find((d) => d.name == amount.loanNumber);
            remappedAmounts.push({
              key: amount.key,
              amount: amount.amount,
              name: amount.loanNumber,
              loanNumber: amount.loanNumber,
              dealName: loan.dealName,
            });
          });

          this.requestData.loanAmounts = remappedAmounts;

          // console.log(remappedAmounts);
          this.doNotExcludeSelectedLoans = true;
        }

        // console.log(this.requestData);
      }

      this.isLoading = false;
    } catch (err: any) {
      this.loggingService.logError(err, SeverityLevel.Error);
      this.isLoading = false;
      notify(err.message, "error", getNotificationTimeout());
    }

    const businessDayQueryModel: IBusinessDayQueryModel = {
      date: this.today.toISOString().split('T')[0],
      tenor: "None",
      location: "US-FD",
      dateRollConvention: "ModifiedFollowing"
    };
    // const businessDayResult: IBusinessDayResultsModel = await lastValueFrom(this.staticDataService.getBusinessDayWithOffset(businessDayQueryModel, 5));
    // this.fifthBusinessDayFromToday = businessDayResult[1].results.dateAdjusted;
    // Emit the value to the BehaviorSubject so reactive subscribers are notified
    // this.fifthBusinessDayFromTodaySubject$.next(this.fifthBusinessDayFromToday);
    // console.log(`Fifth business day from today is ${this.fifthBusinessDayFromToday}`);
    this.isUrgent = false;
    this.mandatoryFiles = false;
    this.billableToClientData = [
      { id: 1, name: "Yes" },
      { id: 2, name: "No" },
    ];
  }
}
